import React, { useState } from 'react';
import { UserPlus, Check, X } from 'lucide-react';
import * as miniLeaguesApi from '../../api/miniLeagues';

/**
 * Pending requests, for the manager.
 *
 * The manager is a player who volunteered, not staff. Anything that makes
 * approving mates a chore goes unactioned and the league sits half empty, so:
 * accept and reject are one tap on the card with no confirm step, and "accept
 * all" is here because a code doing the rounds in a group chat is the normal
 * case, not an edge one.
 *
 * Each card carries the asker's history — it is what tells a mate who mis-tapped
 * from a chancer, and it lets an accidental removal be undone in one tap.
 */
function LeagueJoinRequests({ requests, leagueId, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);
  const [error, setError] = useState('');

  if (!requests || requests.length === 0) return null;

  const run = async (action, id) => {
    setError('');
    setBusyId(id);
    try {
      await action();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.name === 'ApiError' ? err.body?.error || 'That did not work' : 'Something went wrong');
    } finally {
      setBusyId(null);
    }
  };

  const acceptAll = async () => {
    setError('');
    setBusyAll(true);
    try {
      await miniLeaguesApi.acceptAllJoinRequests(leagueId);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.name === 'ApiError' ? err.body?.error || 'That did not work' : 'Something went wrong');
    } finally {
      setBusyAll(false);
    }
  };

  const historyFor = (r) => {
    const bits = [];
    if (r.previouslyRemoved) bits.push('You removed them before');
    if (r.previousRejections > 0) {
      bits.push(
        r.previousRequests > 1
          ? `Asked ${r.previousRequests} times, turned down ${r.previousRejections}`
          : `Turned down ${r.previousRejections} before`
      );
    }
    return bits.join(' · ');
  };

  return (
    <div className="join-requests">
      <div className="join-requests-header">
        <h2 className="league-section-title">
          <UserPlus size={18} /> Wants to join ({requests.length})
        </h2>
        {requests.length > 1 && (
          <button className="primary-btn" onClick={acceptAll} disabled={busyAll}>
            Accept all
          </button>
        )}
      </div>

      {error && <p className="error-text">{error}</p>}

      <div className="join-request-list">
        {requests.map((r) => {
          const history = historyFor(r);
          return (
            <div key={r.id} className="join-request-row">
              <div className="join-request-who">
                <span className="join-request-name">{r.playerName}</span>
                {history && <span className="join-request-history">{history}</span>}
              </div>
              <div className="join-request-actions">
                <button
                  className="primary-btn"
                  disabled={busyId === r.id || busyAll}
                  onClick={() => run(() => miniLeaguesApi.acceptJoinRequest(leagueId, r.id), r.id)}
                  aria-label={`Accept ${r.playerName}`}
                >
                  <Check size={16} />
                </button>
                <button
                  className="secondary-btn"
                  disabled={busyId === r.id || busyAll}
                  onClick={() => run(() => miniLeaguesApi.rejectJoinRequest(leagueId, r.id), r.id)}
                  aria-label={`Decline ${r.playerName}`}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="helper-text">
        Anyone joining after the first game started needs your say-so. Requests
        wait here until you deal with them — they never time out.
      </p>
    </div>
  );
}

export default LeagueJoinRequests;
